import "../CSS/Portafolio.css";
import "../CSS/Buscador.css";
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import Icon from "@mdi/react";
import {
  mdiXml,
  mdiClose,
  mdiOpenInNew,
  mdiMagnify,
  mdiViewGrid,
  mdiViewList,
  mdiChevronLeft,
  mdiChevronRight,
} from "@mdi/js";
import parse from "html-react-parser";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import axios from "axios";
import Preload from "./Preload";
import { gsap } from "../animations/gsap";
import { useLoadingContext } from "../context/LoadingContext";

const apiUrl = process.env.REACT_APP_API_URL;
const PAGE_SIZE = 8;

const Path_Trabajo = require.context("../Images/trabajo", true);
const Path_Trabajos = require.context("../Images/trabajos", true);
const fallbackImage = Path_Trabajos("./logo_work1.svg");

function resolveImage(value, fallback) {
  if (!value) return fallback ? resolveImage(fallback) : null;
  const valStr = String(value).trim();
  if (/^https?:\/\//i.test(valStr)) return valStr;
  const cleanValue = valStr.replace(/^\.\//, "");
  try {
    return Path_Trabajos(`./${cleanValue}`);
  } catch {
    try {
      return Path_Trabajo(`./${cleanValue}`);
    } catch {
      if (fallback && valStr !== fallback) return resolveImage(fallback);
      return null;
    }
  }
}

function resolvePhotos(value, fallback) {
  if (!value) return [resolveImage(null, fallback)];
  return String(value).split(",").map((p) => resolveImage(p.trim(), fallback));
}

function CardImage({ src, alt, fallback }) {
  const [loaded, setLoaded] = useState(false);
  const [imgSrc, setImgSrc] = useState(src);

  useEffect(() => {
    setLoaded(false);
    setImgSrc(src);
  }, [src]);

  return (
    <div className="card-img-wrap">
      {!loaded && <div className="card-img-skeleton" />}
      <img
        src={imgSrc}
        alt={alt}
        loading="lazy"
        decoding="async"
        className={loaded ? "card-img-loaded" : "card-img-hidden"}
        onLoad={() => setLoaded(true)}
        onError={(e) => {
          if (e.currentTarget.src !== fallback) {
            setImgSrc(fallback);
          } else {
            setLoaded(true);
          }
        }}
      />
    </div>
  );
}

const modalSliderSettings = {
  modules: [Navigation, Pagination],
  spaceBetween: 8,
  slidesPerView: 1,
  navigation: true,
  pagination: { clickable: true },
  loop: false,
  style: { width: "100%", height: "460px" },
};

const PortafolioModal = ({ item, onClose }) => {
  const portalEl = document.getElementById("modal-root");
  const [zoomedSrc, setZoomedSrc] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (zoomedSrc) setZoomedSrc(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, zoomedSrc]);

  if (!portalEl) return null;

  return ReactDOM.createPortal(
    <>
      <div className="pm-backdrop" onClick={onClose}>
        <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
          <button className="pm-close" onClick={onClose}>
            <Icon path={mdiClose} size={1} />
          </button>

          <div className="pm-body">
            <div className="pm-image-wrap">
              {item.banners && item.banners.length > 1 ? (
                <Swiper {...modalSliderSettings} className="pm-swiper">
                  {item.banners.map((src, idx) => (
                    <SwiperSlide key={idx}>
                      <img
                        src={src || fallbackImage}
                        alt={`${item.title} - ${idx + 1}`}
                        loading="eager"
                        decoding="async"
                        className="pm-zoomable"
                        style={{
                          width: "100%",
                          height: "400px",
                          objectFit: "contain",
                          display: "block",
                        }}
                        onClick={() => setZoomedSrc(src || fallbackImage)}
                        onError={(e) => {
                          if (e.currentTarget.src !== fallbackImage)
                            e.currentTarget.src = fallbackImage;
                        }}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
              ) : (
                <img
                  src={item.banner || fallbackImage}
                  alt={item.title}
                  loading="eager"
                  decoding="async"
                  className="pm-zoomable"
                  onClick={() => setZoomedSrc(item.banner || fallbackImage)}
                  onError={(e) => {
                    if (e.currentTarget.src !== fallbackImage)
                      e.currentTarget.src = fallbackImage;
                  }}
                />
              )}
            </div>

            <div className="pm-content">
              {item.subtitle && <span className="pm-tag">{item.subtitle}</span>}
              <h2 className="pm-title">{item.title}</h2>
              <div className="pm-description">{parse(item.description)}</div>
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="pm-btn"
              >
                <Icon path={mdiOpenInNew} size={0.85} />
                Ver proyecto
              </a>
            </div>
          </div>
        </div>
      </div>

      {zoomedSrc && (
        <div className="pm-zoom-overlay" onClick={() => setZoomedSrc(null)}>
          <button className="pm-zoom-close" onClick={() => setZoomedSrc(null)}>
            <Icon path={mdiClose} size={1.2} />
          </button>
          <img src={zoomedSrc} alt="zoom" className="pm-zoom-img" />
        </div>
      )}
    </>,
    portalEl
  );
};

const Portafolio = () => {
  const [allItems, setAllItems] = useState([]);
  const [isLoadingItems, setIsLoadingItems] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const gridRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState("Destacados");
  const [viewMode, setViewMode] = useState("list");

  const { register, done } = useLoadingContext();
  const searchRegistered = useRef(false);

  const isSearchActive = debouncedTerm.trim().length > 0;



  useEffect(() => {
    const media = window.matchMedia("(max-width: 768px)");
    const enforce = (e) => { if (e.matches) setViewMode("list"); };
    enforce(media);
    media.addEventListener("change", enforce);
    return () => media.removeEventListener("change", enforce);
  }, []);

  useEffect(() => {
    register();
    axios
      .get(apiUrl + "portfolio")
      .then((response) => {
        const processed = response.data.map((item) => {
          const photoData = item.photo || item.photos;
          const banners = resolvePhotos(photoData, "logo_work1.svg");
          return {
            ...item,
            src: resolveImage(item.logo, "logo_work1.svg"),
            banner: banners[0],
            banners,
          };
        });
        setAllItems(processed);
        setIsLoadingItems(false);
      })
      .catch(console.error)
      .finally(done);
  }, [done, register]);

  useEffect(() => {
    if (isLoadingItems || !gridRef.current) return;
    const items = gridRef.current.querySelectorAll(".item_portafolio");
    const tween = gsap.fromTo(
      items,
      { opacity: 0, scale: 0.7, rotationY: 25, y: 40 },
      {
        opacity: 1,
        scale: 1,
        rotationY: 0,
        y: 0,
        duration: 0.6,
        stagger: { amount: 0.8, from: "start" },
        ease: "back.out(1.2)",
        scrollTrigger: {
          trigger: gridRef.current,
          start: "top 80%",
          once: true,
        },
      }
    );
    return () => {
      tween?.scrollTrigger?.kill();
      tween?.kill();
    };
  }, [isLoadingItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(searchTerm), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!isSearchActive) {
      setSearchResults([]);
      setCurrentPage(1);
      setSelectedCategory("Destacados");
      return;
    }
    setIsSearching(true);
    setCurrentPage(1);
    if (!searchRegistered.current) {
      register();
      searchRegistered.current = true;
    }
    axios
      .get(apiUrl + "portfolio/search?query=" + encodeURIComponent(debouncedTerm))
      .then((response) => {
        const processed = response.data
          .map((item) => ({ ...item, src: resolveImage(item.logo) }))
          .sort((a, b) => {
            const dateA = new Date(a.date || a.createdAt || a.created_at || 0);
            const dateB = new Date(b.date || b.createdAt || b.created_at || 0);
            if (dateB - dateA !== 0) return dateB - dateA;
            return (b.id ?? 0) - (a.id ?? 0);
          });
        setSearchResults(processed);
        setSelectedCategory("Todos"); // reset to Todos on each new search
      })
      .catch(console.error)
      .finally(() => setIsSearching(false));
  }, [debouncedTerm, isSearchActive, register]);

  const destacados = useMemo(
    () => allItems.filter((item) => Number(item.destaque) === 1),
    [allItems]
  );

  const categories = useMemo(() => {
    if (isSearchActive) {
      const cats = searchResults.map((item) => item.subtitle).filter(Boolean);
      return ["Todos", ...new Set(cats)];
    } else {
      const cats = allItems.map((item) => item.subtitle).filter(Boolean);
      return ["Destacados", "Todos", ...new Set(cats)];
    }
  }, [searchResults, allItems, isSearchActive]);

  const filtered = useMemo(() => {
    if (selectedCategory === "Todos") return searchResults;
    return searchResults.filter((item) => item.subtitle === selectedCategory);
  }, [searchResults, selectedCategory]);

  const filteredDestacados = useMemo(() => {
    if (selectedCategory === "Todos") return allItems;
    if (selectedCategory === "Destacados") return destacados;
    return allItems.filter((item) => item.subtitle === selectedCategory);
  }, [destacados, allItems, selectedCategory]);

  const allTotalPages = Math.ceil(filteredDestacados.length / PAGE_SIZE);
  const allPaginated = useMemo(() => {
    return filteredDestacados.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  }, [filteredDestacados, currentPage]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const pageRange = useMemo(() => {
    if (totalPages <= 5)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (currentPage <= 3) return [1, 2, 3, 4, 5];
    if (currentPage >= totalPages - 2)
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
  }, [currentPage, totalPages]);

  const goToPage = (page) => {
    setCurrentPage(page);
    document
      .getElementById("portafolio")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSelectedCategory("Destacados");
    setCurrentPage(1);
  };

  const onOpenModal = useCallback((item) => setSelectedItem(item), []);
  const onCloseModal = useCallback(() => setSelectedItem(null), []);

  if (isLoadingItems) return <Preload />;

  return (
    <div id="portafolio" className="content_box">
      <div className="buscador-header">
        <h1 className="content_box_title">
          <Icon path={mdiXml} className="Main_Icons" size={1} />
          Portafolio
        </h1>
        {!isSearchActive ? (
          filteredDestacados.length > 0 && <span className="buscador-count">{filteredDestacados.length}</span>
        ) : (
          !isSearching && <span className="buscador-count">{filtered.length}</span>
        )}
      </div>

      <div className="buscador-search-row">
        <div className="content_box_buscador">
          <Icon path={mdiMagnify} className="buscador-search-icon" size={0.9} />
          <input
            type="text"
            placeholder="Buscar en el portafolio..."
            className="content_box_buscador_input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="buscador-clear-btn" onClick={clearSearch}>
              <Icon path={mdiClose} size={0.8} />
            </button>
          )}
        </div>

        {searchTerm.length > 0 && (
          <div className="buscador-view-toggle">
            <button
              className={`toggle-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              title="Vista lista"
            >
              <Icon path={mdiViewList} size={1} />
            </button>
            <button
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              title="Vista grilla"
            >
              <Icon path={mdiViewGrid} size={1} />
            </button>
          </div>
        )}
      </div>

      {/* Chips de categorías — siempre visibles */}
      {categories.length > 1 && (
        <div className="buscador-categories">
          {categories.map((cat) => (
            <button
              key={cat}
              className={`category-chip ${selectedCategory === cat ? "active" : ""}`}
              onClick={() => {
                setSelectedCategory(cat);
                setCurrentPage(1);
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {!searchTerm && (
        <>
          <p className="portafolio-destacados-label">
            {selectedCategory === "Todos"
              ? `Todos los proyectos (${allItems.length})`
              : selectedCategory === "Destacados"
              ? "Proyectos destacados"
              : selectedCategory}
          </p>
          <>
            <div ref={gridRef} className="grid_portafolio">
              {allPaginated.map((item, index) => (
                  <div
                    key={item.id ?? index}
                    className="item_portafolio"
                    onClick={() => onOpenModal(item)}
                  >
                    <img
                      className="item_image_portafolio"
                      src={item.src}
                      alt={item.title}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        if (e.currentTarget.src !== fallbackImage)
                          e.currentTarget.src = fallbackImage;
                      }}
                    />
                    <div className="item_cortina">
                      <div className="item_cortina_text">{item.title}</div>
                      {item.subtitle && (
                        <div className="item_cortina_tag">{item.subtitle}</div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
            {allTotalPages > 1 && (
                <div className="buscador-pagination">
                  <button className="page-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                    <Icon path={mdiChevronLeft} size={0.9} />
                  </button>
                  {Array.from({ length: allTotalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === allTotalPages || Math.abs(p - currentPage) <= 2)
                    .reduce((acc, p, i, arr) => {
                      if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, i) =>
                      p === "..." ? (
                        <span key={`dots-${i}`} className="page-dots">&hellip;</span>
                      ) : (
                        <button
                          key={p}
                          className={`page-btn ${currentPage === p ? "active" : ""}`}
                          onClick={() => goToPage(p)}
                        >{p}</button>
                      )
                    )}
                <button className="page-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === allTotalPages}>
                  <Icon path={mdiChevronRight} size={0.9} />
                </button>
              </div>
            )}
          </>
        </>
      )}

      {searchTerm && (
        <div className="portafolio-results-area">
          <div className="buscador-meta">
            {isSearching ? (              <span className="buscador-loading">Buscando...</span>
            ) : (
              <span>
                {filtered.length} resultado{filtered.length !== 1 ? "s" : ""}
                {totalPages > 1 && <> &nbsp;&middot;&nbsp; P&aacute;gina {currentPage} de {totalPages}</>}
              </span>
            )}
          </div>

          <div className={`content_box_results ${viewMode === "grid" ? "results-grid" : ""}`}>
            {!isSearching && filtered.length === 0 ? (
              <div className="no-results-message">No se encontraron resultados</div>
            ) : (
              paginated.map((item, index) => (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`Card ${viewMode === "grid" ? "Card-grid" : ""}`}
                  key={item.id ?? index}
                >
                  <div className="Card-Image">
                    <CardImage src={item.src} alt={item.title} fallback={fallbackImage} />
                  </div>
                  <div className="Card-text">
                    <h3>{item.title}</h3>
                    <div className="Card-description">{parse(item.description)}</div>
                  </div>
                  <div className="Card-footer">
                    {item.subtitle && <span className="card-tag">{item.subtitle}</span>}
                  </div>
                </a>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div className="buscador-pagination">
              <button
                className="page-btn"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <Icon path={mdiChevronLeft} size={0.9} />
              </button>

              {pageRange[0] > 1 && (
                <>
                  <button className="page-btn" onClick={() => goToPage(1)}>1</button>
                  {pageRange[0] > 2 && <span className="page-dots">�</span>}
                </>
              )}

              {pageRange.map((page) => (
                <button
                  key={page}
                  className={`page-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}

              {pageRange[pageRange.length - 1] < totalPages && (
                <>
                  {pageRange[pageRange.length - 1] < totalPages - 1 && (
                    <span className="page-dots">�</span>
                  )}
                  <button className="page-btn" onClick={() => goToPage(totalPages)}>
                    {totalPages}
                  </button>
                </>
              )}

              <button
                className="page-btn"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <Icon path={mdiChevronRight} size={0.9} />
              </button>
            </div>
          )}
        </div>
      )}

      {selectedItem && (
        <PortafolioModal item={selectedItem} onClose={onCloseModal} />
      )}
    </div>
  );
};

export default Portafolio;
